import { z } from 'zod';
import {
  alcoholConsumptions,
  familyStatuses,
  handPreferences,
  languages,
  noteImportances,
  patientRelationshipTypes,
  patientSexes,
  quantityPeriods,
  referralSources,
  smokerStatuses,
  ssnCountries,
  type FamilyStatus,
  type Language,
  type NoteImportance,
  type PatientRelationshipType,
  type PatientSex,
  type ReferralSource,
  type SsnCountry,
} from '../enums.js';
import { SSN_MAX_LENGTH } from '../validation/ssn.js';
import { residenceCountrySchema, type Iso3166Alpha2 } from '../validation/country.js';
import { dateOnlySchema } from './_fields.js';
import type { HistoryEntryDto } from './patients-history.js';

export { dateOnlySchema } from './_fields.js';
export {
  historyCreateSchema,
  historyUpdateSchema,
  HISTORY_ACTIVITY_ERRORS,
  HISTORY_YEAR_ERRORS,
  HISTORY_YEAR_MIN,
  historyActivityIssue,
  historyYearIssue,
  type HistoryOnsetValue,
  type HistoryYearIssue,
  type HistoryActivity,
  type HistoryActivityIssue,
  type HistoryCreateInput,
  type HistoryUpdateInput,
  type HistoryEntryDto,
} from './patients-history.js';

/**
 * Contrat réseau du module `patients`, partagé mot pour mot entre le serveur et le front.
 */

/* -------------------------------------------------------------------------- */
/* Entrées : schémas stricts écrits à la main, jamais dérivés d'une table.      */
/* -------------------------------------------------------------------------- */

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable();

const patientBase = z.object({
  firstName: trimmed(100),
  lastName: trimmed(100),
  birthDate: dateOnlySchema,
  sex: z.enum(patientSexes).nullable(),
  /**
   * Numéro de sécurité sociale et le pays qui le délivre — CNS, NIR, NISS ou autre —, comme le
   * sélecteur de pays de la V1 devant le champ. Le numéro est gardé **normalisé** (sans
   * séparateurs, voir `normalizeSsn`). Sa cohérence avec le pays porte sur deux champs à la fois,
   * ce qu'un schéma de champ ne sait pas dire : elle se vérifie par `ssnIssue`, au service côté
   * serveur et au formulaire côté écran, une seule règle pour les deux.
   */
  ssnCountry: z.enum(ssnCountries),
  ssn: optionalText(SSN_MAX_LENGTH),
  profession: optionalText(120),
  preferredLanguage: z.enum(languages).nullable(),
  email: z.email().max(254).nullable(),
  phone: optionalText(40),
  mobilePhone: optionalText(40),
  street: optionalText(160),
  houseNumber: optionalText(20),
  addressDetails: optionalText(160),
  postalCode: optionalText(20),
  city: optionalText(120),
  /** Pays de résidence, pas le pays du cabinet : un frontalier réside en FR, BE ou DE. */
  country: residenceCountrySchema,
  referralSource: z.enum(referralSources).nullable(),
  familyStatus: z.enum(familyStatuses).nullable(),
  numberOfChildren: z.int().min(0).max(30).nullable(),
  primaryPhysician: optionalText(160),
  companyName: optionalText(160),
  vatNumber: optionalText(30),
  noteImportance: z.enum(noteImportances),
  notesAdmin: optionalText(5000),
  /** Note du patient, distincte des notes administratives. */
  patientNote: optionalText(5000),
  sharingConsent: z.boolean(),
  /** Faux quand le patient refuse les rappels de rendez-vous par courriel. Vrai par défaut. */
  acceptsAppointmentReminders: z.boolean(),
});

/* -------------------------------------------------------------------------- */
/* Sous-ressources du dossier                                                  */
/* -------------------------------------------------------------------------- */

/*
 * Déclarées **avant** la création du dossier, qui les compose : le formulaire « Nouveau
 * patient » de la V1 posait mode de vie et liens familiaux d'un seul geste, et la V2 le rend.
 * Ce sont les mêmes schémas que les routes dédiées emploient — un seul contrat par chose.
 */

export const lifestyleSchema = z
  .object({
    smokerStatus: z.enum(smokerStatuses).nullable().optional(),
    smokerQuantity: z.int().min(0).max(200).nullable().optional(),
    smokerQuantityPeriod: z.enum(quantityPeriods).nullable().optional(),
    alcoholConsumption: z.enum(alcoholConsumptions).nullable().optional(),
    alcoholQuantity: z.int().min(0).max(200).nullable().optional(),
    alcoholQuantityPeriod: z.enum(quantityPeriods).nullable().optional(),
    handPreference: z.enum(handPreferences).nullable().optional(),
    referenceHeight: z.number().min(0).max(300).nullable().optional(),
    referenceWeight: z.number().min(0).max(500).nullable().optional(),
    physicalActivities: z
      .array(z.object({ activity: trimmed(80), frequency: trimmed(40) }).strict())
      .max(20)
      .optional(),
    currentMedications: optionalText(2000).optional(),
    allergies: optionalText(2000).optional(),
  })
  .strict();
export type LifestyleInput = z.infer<typeof lifestyleSchema>;

export const relationshipCreateSchema = z
  .object({
    relatedPatientId: z.uuid(),
    relationshipType: z.enum(patientRelationshipTypes),
    notes: optionalText(1000).optional(),
    shareAntecedents: z.boolean().optional(),
  })
  .strict();
export type RelationshipCreateInput = z.infer<typeof relationshipCreateSchema>;

/** Seuls l'identité et la date de naissance sont exigées : c'est la création rapide de la V1. */
export const patientCreateSchema = patientBase
  .partial()
  .extend({
    firstName: trimmed(100),
    lastName: trimmed(100),
    birthDate: dateOnlySchema,
    /** Le client rejoue la création après avoir vu les doublons proposés. */
    acknowledgeDuplicates: z.boolean().optional(),
    /** Paires à ne plus jamais signaler. */
    ignoreDuplicateOf: z.array(z.uuid()).max(20).optional(),
    /**
     * Le mode de vie, posé du même geste — les cartes « Activités physiques » et « Informations
     * médicales » du formulaire de la V1. Facultatif : la création rapide reste possible, et le
     * mode de vie s'enregistre aussi bien plus tard par `PUT /:id/lifestyle`.
     */
    lifestyle: lifestyleSchema.optional(),
    /**
     * Les liens familiaux vers des dossiers **existants du cabinet**, carte « Informations
     * complémentaires » de la V1. Chaque lien crée sa réciproque, comme la route dédiée. Un lien
     * vers un dossier inconnu fait échouer toute la création : on ne veut pas d'un dossier créé
     * à moitié, sans les liens que le praticien croyait poser.
     *
     * Dix au plus : c'est une fratrie large, pas un carnet d'adresses.
     */
    relationships: z.array(relationshipCreateSchema).max(10).optional(),
  })
  .strict();
export type PatientCreateInput = z.infer<typeof patientCreateSchema>;

export const patientUpdateSchema = patientBase.partial().strict();
export type PatientUpdateInput = z.infer<typeof patientUpdateSchema>;

/**
 * Critères de tri de la liste patients. Trois, et trois seulement : ce sont ceux qu'un index
 * couvre. Exposer un tri que la base ne sait pas servir se paierait sur chaque page dès le
 * millier de dossiers.
 *
 * « Dernière visite » n'y est pas, sciemment. La V1 l'affichait dans son sélecteur et triait en
 * réalité sur la date de modification du dossier — deux choses différentes, et le libellé mentait.
 * Le vrai critère demanderait d'agréger les consultations par dossier à chaque page ; il attend
 * une colonne dénormalisée, notée au backlog.
 */
export const patientSortFields = ['name', 'created', 'updated'] as const;
export type PatientSortField = (typeof patientSortFields)[number];

export const sortDirections = ['asc', 'desc'] as const;
export type SortDirection = (typeof sortDirections)[number];

export const patientListQuerySchema = z
  .object({
    search: z.string().trim().max(120).optional(),
    archived: z.enum(['false', 'true', 'all']).default('false'),
    /**
     * Ignoré tant qu'une recherche est en cours : la pertinence prime alors, et un tri
     * alphabétique par-dessus ferait remonter des correspondances lointaines avant les bonnes.
     */
    sort: z.enum(patientSortFields).default('name'),
    direction: z.enum(sortDirections).default('asc'),
    /** Dossiers créés à partir de cette date incluse : le « nouveaux patients » de la V1, en vrai. */
    createdFrom: dateOnlySchema.optional(),
    /** Dossiers créés jusqu'à cette date incluse. */
    createdTo: dateOnlySchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
  })
  .strict()
  .refine(
    (q) => q.createdFrom === undefined || q.createdTo === undefined || q.createdFrom <= q.createdTo,
    {
      error: 'La fin de la période doit suivre son début',
      path: ['createdTo'],
    },
  );
export type PatientListQuery = z.infer<typeof patientListQuerySchema>;

export const patientIdParamSchema = z.object({ id: z.uuid() }).strict();

export const patientMergeSchema = z.object({ sourceId: z.uuid(), targetId: z.uuid() }).strict();

/** Corps de `POST /api/patients/:id/verify-birthdate` — date civile uniquement, jamais d’autre champ. */
export const patientVerifyBirthdateSchema = z.object({ birthDate: dateOnlySchema }).strict();
export type PatientVerifyBirthdateInput = z.infer<typeof patientVerifyBirthdateSchema>;

/* -------------------------------------------------------------------------- */
/* Sorties : objets de transfert explicites. Une ligne Drizzle ne sort jamais.  */
/* -------------------------------------------------------------------------- */

export interface PatientSummaryDto {
  id: string;
  firstName: string;
  lastName: string;
  /**
   * Nulle quand l'appelant ne peut pas ouvrir le dossier : la date est la clé de la garde
   * entre collègues, elle ne figure pas dans la liste.
   */
  birthDate: string | null;
  ssn: string | null;
  ssnCountry: SsnCountry;
  email: string | null;
  phone: string | null;
  mobilePhone: string | null;
  archivedAt: string | null;
  /**
   * Effacement RGPD exécuté : l'identité du dossier a été remplacée, mais consultations et pièces
   * comptables restent sous rétention légale. Exposé parce que l'écran doit pouvoir **dire** que
   * les champs sont vides parce qu'ils ont été effacés, au lieu de le faire deviner.
   */
  anonymizedAt: string | null;
  isTemporary: boolean;
}

export interface PatientDto extends PatientSummaryDto {
  sex: PatientSex | null;
  profession: string | null;
  preferredLanguage: Language | null;
  street: string | null;
  houseNumber: string | null;
  addressDetails: string | null;
  postalCode: string | null;
  city: string | null;
  /** Code ISO 3166-1 alpha-2 du pays de résidence. `LU` par défaut. */
  country: Iso3166Alpha2;
  referralSource: ReferralSource | null;
  familyStatus: FamilyStatus | null;
  numberOfChildren: number | null;
  primaryPhysician: string | null;
  companyName: string | null;
  vatNumber: string | null;
  noteImportance: NoteImportance;
  notesAdmin: string | null;
  /** Note saisie par le patient. Jamais le contenu de `notesAdmin`. */
  patientNote: string | null;
  sharingConsent: boolean;
  sharingConsentAt: string | null;
  acceptsAppointmentReminders: boolean;
  deletedAt: string | null;
  temporaryExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RelationshipDto {
  id: string;
  patientId: string;
  relatedPatient: PatientSummaryDto;
  relationshipType: PatientRelationshipType;
  notes: string | null;
  shareAntecedents: boolean;
  createdAt: string;
}

/**
 * Antécédents d'un proche dont le lien familial autorise le partage.
 * Un lien sans `shareAntecedents` n'apparaît jamais ici : rien n'est inventé depuis le seul drapeau.
 * Lecture limitée au cabinet de la session (RLS + sous-ressources patient).
 */
export interface SharedAntecedentGroupDto {
  relationshipId: string;
  relationshipType: PatientRelationshipType;
  relatedPatient: PatientSummaryDto;
  entries: HistoryEntryDto[];
}

export interface LifestyleDto {
  patientId: string;
  smokerStatus: string | null;
  smokerQuantity: number | null;
  smokerQuantityPeriod: string | null;
  alcoholConsumption: string | null;
  alcoholQuantity: number | null;
  alcoholQuantityPeriod: string | null;
  handPreference: string | null;
  referenceHeight: number | null;
  referenceWeight: number | null;
  physicalActivities: { activity: string; frequency: string }[];
  currentMedications: string | null;
  allergies: string | null;
}

/* -------------------------------------------------------------------------- */
/* Doublons                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Panneau des doublons probables du cabinet. La réservation publique crée un dossier par
 * réservation quand le patient ne se reconnaît pas dans la recherche : deux dossiers pour la même
 * personne, chacun avec une moitié de son histoire.
 *
 * La détection est **une proposition, jamais une décision** : elle rapproche, elle ne fusionne
 * pas. La fusion reste un geste explicite sur une paire choisie.
 */
export const duplicateScanQuerySchema = z
  .object({
    /** Score minimal retenu, sur cent. En dessous, le rapprochement tient du hasard. */
    minScore: z.coerce.number().int().min(30).max(100).default(50),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .strict();
export type DuplicateScanQuery = z.infer<typeof duplicateScanQuerySchema>;

/** Signaux qui ont rapproché deux dossiers. Le front les affiche pour que le praticien juge. */
export const duplicateSignals = ['ssn', 'name', 'birthDate', 'phone', 'address'] as const;
export type DuplicateSignal = (typeof duplicateSignals)[number];

export interface DuplicatePairDto {
  /** Les deux dossiers, le plus ancien d'abord : c'est le candidat naturel à conserver. */
  patients: [PatientSummaryDto, PatientSummaryDto];
  /** Sur cent. Cent = même numéro de sécurité sociale, même nom, même date de naissance. */
  score: number;
  signals: DuplicateSignal[];
}

export interface DuplicateScanDto {
  pairs: DuplicatePairDto[];
  /** Dossiers examinés. Au-delà du plafond, le balayage s'arrête et le dit. */
  scanned: number;
  /** Vrai quand le cabinet compte plus de dossiers que le balayage n'en examine en une fois. */
  truncated: boolean;
}
